var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/utils/createApiQueryClient.ts
import { QueryClient } from "@tanstack/react-query";
import deepmerge from "deepmerge";
var BASE_CONFIG = {
  defaultOptions: {
    queries: {
      gcTime: 1e3 * 60 * 30
      // 30 minutes
    }
  }
};
var createApiQueryClient = (config) => {
  return new QueryClient(deepmerge(BASE_CONFIG, config ?? {}));
};

// src/utils/createBaseApi.ts
import deepmerge2 from "deepmerge";
function mergeHeaders(headerA, headerB) {
  const newHeaders = { ...headerA, ...headerB };
  return Object.fromEntries(
    Object.entries(newHeaders).filter(([, value]) => value !== null)
  );
}
function mergeFetchOptions(defaults, overrides) {
  const mergedOptions = deepmerge2(defaults, overrides, {
    customMerge: (key) => {
      if (key === "headers") {
        return mergeHeaders;
      }
    }
  });
  return {
    ...mergedOptions,
    body: overrides.body instanceof FormData ? overrides.body : mergedOptions.body
  };
}
var createBaseApi = (baseUrl = "", defaultInit = {}, onError) => {
  const baseInit = {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  };
  const resolvedDefaultInit = deepmerge2(baseInit, defaultInit);
  return async (resource, options = {}, config) => {
    const res = await fetch(
      `${baseUrl}${resource}`,
      mergeFetchOptions(resolvedDefaultInit, options)
    );
    if (!res.ok) {
      onError?.(res);
      return {
        data: null,
        error: true,
        res
      };
    }
    if (res.status === 204) {
      return {
        data: null,
        error: false,
        res
      };
    }
    const transformedData = await (config?.responseTransform?.(res) ?? res.json().catch(() => void 0));
    return {
      data: transformedData,
      error: false,
      res
    };
  };
};

// src/clients/dashboard/createDashboardApiClient.ts
var executeQuery = (ctx) => async (params) => {
  if (ctx.invalidateQueries?.length) {
    const response = await ctx.fetchFunction(params);
    if (response.error) {
      const error = new Error("Failed to fetch");
      Object.assign(error, { res: response.res });
      throw error;
    }
    return response;
  }
  const fetchRes = await ctx.apiQueryClient.fetchQuery({
    queryFn: async () => {
      const response = await ctx.fetchFunction(params);
      if (response.error) {
        const error = new Error("Failed to fetch");
        Object.assign(error, { res: response.res });
        throw error;
      }
      return response;
    },
    queryKey: [...ctx.queryKey, ...params ? [params] : [], "legacy"]
  });
  ctx.apiQueryClient.setQueryData(
    [...ctx.queryKey, ...params ? [params] : []],
    fetchRes.data
  );
  ctx.apiQueryClient.removeQueries({
    queryKey: [...ctx.queryKey, ...params ? [params] : [], "legacy"]
  });
  return fetchRes;
};
var createDashboardApiClient = (config) => {
  const dashboardApiClient = {};
  const initializeEndpoints = (ctx) => (baseApi) => {
    return Object.entries(config.endpoints).forEach(([key, value]) => {
      const fetchFunction = value(baseApi);
      const queryKey = [
        ...ctx.baseQueryKey ? [ctx.baseQueryKey] : [],
        ...value?.queryKey ?? ["No query key found"],
        config.version
      ];
      const invalidateQueries = (value.invalidateQueries ?? []).map(
        (queryToInvalidate) => [
          ...Array.isArray(queryToInvalidate) ? queryToInvalidate : [queryToInvalidate]
        ]
      );
      const query = async (params) => {
        try {
          const queryResponse = await executeQuery({
            apiQueryClient: ctx.apiQueryClient,
            fetchFunction,
            queryKey,
            invalidateQueries
          })(params);
          await Promise.all(
            invalidateQueries.map((queryKeyToInvalidate) => {
              const stringifiedQueryKey = typeof queryKeyToInvalidate === "string" ? queryKeyToInvalidate : queryKeyToInvalidate.join(".");
              ctx.apiQueryClient.invalidateQueries({
                predicate: (query2) => {
                  return query2.queryKey.join(".").includes(stringifiedQueryKey);
                }
              });
            })
          );
          return queryResponse;
        } catch (e) {
          return {
            error: true,
            /**
             * If we don't have res on the object, we error'd real hard. However, some downstream consumers
             * of this information may want to be looking at the status of the response to understand what
             * error path to take. We, therefore, return a 500 status here as it's the closest proxy we
             * have to what has likely happened (some kind of network/server error that if you refresh, may work).
             */
            res: e && typeof e === "object" && "res" in e ? e.res : { status: 500 },
            data: null
          };
        }
      };
      Object.assign(query, {
        request: fetchFunction,
        queryKey,
        mocking: value.mocking,
        method: value.method,
        version: config.version,
        invalidateQueries
      });
      dashboardApiClient[key] = query;
    }, {});
  };
  const initialize = (args) => {
    const apiOrigin = args.apiOrigin ?? config.apiOrigin ?? `${import.meta.env?.VITE_API_ORIGIN ?? ""}/api`;
    const baseApi = args.isDemoEnv ? createBaseApi(apiOrigin, {
      headers: {
        "Sequence-Version": config.version,
        Authorization: `Basic ${args.demoApiKey}`,
        ...args.defaultHeaders ?? {}
      }
    }) : createBaseApi(apiOrigin, {
      headers: {
        "Sequence-Version": config.version,
        ...args.defaultHeaders ?? {}
      }
    });
    initializeEndpoints({
      apiQueryClient: args.apiQueryClient ?? createApiQueryClient(),
      baseQueryKey: args.baseQueryKey
    })(baseApi);
  };
  initialize({});
  dashboardApiClient.initialize = initialize;
  return dashboardApiClient;
};

// src/utils/createEndpoints.ts
var createQueryEndpoint = (args) => {
  const readyForClientFn = (apiClient) => {
    const endpointHandler = (params) => {
      return apiClient(
        args.route(params),
        {
          method: args.method ?? "GET",
          body: args.method === "POST" && args.body ? args.body(params) : null,
          ...args.requestOptions
        },
        args.config
      );
    };
    return endpointHandler;
  };
  readyForClientFn.mocking = args.mocking && {
    ...args.mocking,
    status: args.mocking?.status ?? 200
  };
  readyForClientFn.method = args.method ?? "GET";
  readyForClientFn.queryKey = args.queryKey;
  return readyForClientFn;
};
var createMutationEndpoint = (args) => {
  const readyForClientFn = (apiClient) => {
    const endpointHandler = (params) => {
      return apiClient(
        args.route(params),
        {
          ...args.requestOptions,
          method: args.method,
          body: args.body ? args.body(params) : null
        },
        args.config
      );
    };
    return endpointHandler;
  };
  readyForClientFn.invalidateQueries = args.invalidateQueries;
  readyForClientFn.method = args.method;
  readyForClientFn.mocking = args.mocking && {
    ...args.mocking,
    status: args.mocking?.status ?? 200
  };
  return readyForClientFn;
};

// src/clients/dashboard/v20240509/endpoints/integrations/integrations.cacheKeys.ts
var INTEGRATIONS = "integrations";
var INTEGRATION_APP_TOKEN = "integrationAppToken";
var INTEGRATION_OAUTH_REDIRECT = "integrationOAuthRedirect";

// src/clients/dashboard/v20240509/endpoints/integrations/getIntegrations/getIntegrations.api.ts
var getIntegrations = createQueryEndpoint({
  queryKey: [INTEGRATIONS],
  route: () => `/integrations`,
  mocking: {
    match: /\/api\/integrations/,
    mock: {
      /**
       * We don't want to return any integration information in the default mock here. This
       * is because integrations in general are rather impactful, and we don't want the fallback
       * mock to influence the behaviour of any given screen in weird and wonderful ways.
       *
       * Other endpoints, even list endpoints like getCustomers, are much less influencial when
       * called 'incidentally'. But not so much for this one!
       */
      items: []
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/prices/prices.cacheKeys.ts
var LIST_PRICES = "listPrices";
var PRICES = "prices";

// src/clients/dashboard/v20240509/endpoints/prices/getListPrice/getListPrice.api.ts
var getListPrice = createQueryEndpoint({
  route: (params) => `/list-prices/${params.id}`,
  queryKey: [LIST_PRICES],
  mocking: {
    match: /\/api\/list-prices\/(.+)/,
    mock: {
      id: "price-1",
      name: "Example One Time Price",
      productId: "product-1",
      structure: {
        pricingType: "ONE_TIME",
        price: "10.00"
      },
      currency: "USD",
      createdAt: "2024-06-11",
      integrationIds: [
        {
          service: "Xero",
          id: "foo"
        }
      ],
      billingFrequency: "ONE_TIME",
      billingType: "IN_ARREARS",
      customMetricParameters: []
    }
  }
});

// src/utils/createQueryParams.ts
var createQueryParams = (params) => {
  if (!params) {
    return "";
  }
  const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value === null || value === void 0) {
      return acc;
    }
    return {
      ...acc,
      [key]: value.toString()
    };
  }, {});
  if (Object.keys(filteredParams).length === 0) {
    return "";
  }
  return `?${new URLSearchParams(filteredParams).toString()}`;
};

// src/clients/dashboard/v20240509/endpoints/prices/getListPrices/getListPrices.api.ts
var getListPrices = createQueryEndpoint(
  {
    route: (params) => `/list-prices${createQueryParams(params)}`,
    queryKey: [LIST_PRICES],
    mocking: {
      match: /\/api\/list-prices(\?(.*))?$/,
      mock: {
        items: [
          {
            id: "price-1",
            name: "Example One Time Price",
            productId: "product-1",
            structure: {
              pricingType: "ONE_TIME",
              price: "10.00"
            },
            currency: "USD",
            createdAt: "2024-06-11",
            integrationIds: [
              {
                service: "Xero",
                id: "foo"
              }
            ],
            billingFrequency: "ONE_TIME",
            billingType: "IN_ARREARS",
            customMetricParameters: []
          }
        ],
        pagination: {
          after: "",
          before: "",
          totalResultSize: 1
        }
      }
    }
  }
);

// src/clients/dashboard/v20240509/endpoints/prices/getPrice/getPrice.api.ts
var getPrice = createQueryEndpoint({
  route: (params) => `/prices/${params.id}`,
  queryKey: [PRICES],
  mocking: {
    match: /\/api\/prices\/(.+)/,
    mock: {
      id: "price-1",
      name: "Example One Time Price",
      productId: "product-1",
      structure: {
        pricingType: "ONE_TIME",
        price: "10.00"
      },
      currency: "USD",
      createdAt: "2024-06-11",
      integrationIds: [
        {
          service: "Xero",
          id: "foo"
        }
      ],
      billingFrequency: "ONE_TIME",
      billingType: "IN_ARREARS",
      customMetricParameters: []
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/prices/getPrices/getPrices.api.ts
var getPrices = createQueryEndpoint({
  route: (params) => `/prices${createQueryParams(params)}`,
  queryKey: [PRICES],
  mocking: {
    match: /\/api\/prices(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "price-1",
          name: "Example One Time Price",
          productId: "product-1",
          structure: {
            pricingType: "ONE_TIME",
            price: "10.00"
          },
          currency: "USD",
          createdAt: "2024-06-11",
          integrationIds: [
            {
              service: "Xero",
              id: "foo"
            }
          ],
          billingFrequency: "ONE_TIME",
          billingType: "IN_ARREARS",
          customMetricParameters: []
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/products/products.cacheKeys.ts
var PRODUCTS = "products";
var PRODUCT_METADATA = "productMetadata";

// src/clients/dashboard/v20240509/endpoints/products/getProducts/getProducts.api.ts
var getProducts = createQueryEndpoint({
  route: (params) => `/products${createQueryParams(params)}`,
  queryKey: [PRODUCTS],
  mocking: {
    match: /\/api\/products(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "product-1",
          name: "Example Product",
          label: "example-product",
          createdAt: "2024-06-11",
          updatedAt: "2024-06-11"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/products/getProduct/getProduct.api.ts
var getProduct = createQueryEndpoint({
  route: ({ id }) => `/products/${id}`,
  queryKey: [PRODUCTS],
  mocking: {
    match: /\/api\/products\/(.+)/,
    mock: {
      id: "product-1",
      name: "Example Product",
      label: "example-product",
      createdAt: "2024-06-11",
      updatedAt: "2024-06-11"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/products/getProductMetadata/getProductMetadata.api.ts
var getProductMetadata = createQueryEndpoint({
  route: ({ id }) => `/products/${id}/metadata`,
  queryKey: [PRODUCT_METADATA],
  mocking: {
    match: /\/api\/products\/(.+)\/metadata$/,
    mock: {
      priceVariants: [
        {
          id: "1",
          name: "Demo Product",
          productId: "product-1",
          structure: {
            pricingType: "ONE_TIME",
            price: "150.00"
          },
          currency: "GBP",
          integrationIds: [{ service: "Stripe", id: "1" }],
          billingFrequency: "MONTHLY",
          billingType: "IN_ARREARS",
          customMetricParameters: [],
          createdAt: "2021-01-01T00:00:00Z",
          listPriceId: "1"
        },
        {
          id: "2",
          name: "Demo Product",
          productId: "product-1",
          structure: {
            pricingType: "ONE_TIME",
            price: "1.00"
          },
          currency: "GBP",
          integrationIds: [{ service: "Xero", id: "123" }],
          billingFrequency: "ONE_TIME",
          billingType: "IN_ADVANCE",
          customMetricParameters: [],
          createdAt: "2021-01-01T00:00:00Z",
          listPriceId: "2"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/sequenceAccount/sequenceAccount.cacheKeys.ts
var SEQUENCE_ACCOUNT_SETTINGS = "sequenceAccountSettings";

// src/clients/dashboard/v20240509/endpoints/sequenceAccount/getSequenceAccountSettings/getSequenceAccountSettings.api.ts
var getSequenceAccountSettings = createQueryEndpoint({
  route: () => `/sequence-account-settings`,
  queryKey: [SEQUENCE_ACCOUNT_SETTINGS],
  mocking: {
    match: /\/api\/sequence-account-settings/,
    mock: {
      id: "settings-1",
      sequenceAccountId: "sequence-account-1",
      enabledCurrencies: ["USD", "EUR"]
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/taxRates/taxRates.cacheKeys.ts
var TAX_RATES = "taxRates";

// src/clients/dashboard/v20240509/endpoints/taxRates/getTaxRate/getTaxRate.api.ts
var getTaxRate = createQueryEndpoint({
  route: ({ id }) => `/tax-rates/${id}`,
  queryKey: [TAX_RATES],
  mocking: {
    match: /\/api\/tax-rates\/(.+)/,
    mock: {
      id: "tax-rate-1",
      name: "string",
      invoiceName: "string",
      invoiceShortName: "string",
      description: "string",
      amount: "0.2",
      country: "GB",
      isArchived: false
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/taxRates/getTaxRates/getTaxRates.api.ts
var getTaxRates = createQueryEndpoint({
  route: (params) => `/tax-rates${createQueryParams(params)}`,
  queryKey: [TAX_RATES],
  mocking: {
    match: /\/api\/tax-rates(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "string",
          name: "string",
          invoiceName: "string",
          invoiceShortName: "string",
          description: "string",
          amount: "0.2",
          country: "GB",
          isArchived: false
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/prices/postPrice/postPrice.api.ts
var postPrice = createMutationEndpoint({
  route: () => `/prices`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [[PRICES], [PRODUCT_METADATA]],
  mocking: {
    match: /\/api\/prices$/,
    mock: {
      id: "price-1",
      name: "Example One Time Price",
      productId: "product-1",
      structure: {
        pricingType: "ONE_TIME",
        price: "10.00"
      },
      currency: "USD",
      createdAt: "2024-06-11",
      integrationIds: [
        {
          service: "Xero",
          id: "foo"
        }
      ],
      billingFrequency: "ONE_TIME",
      billingType: "IN_ARREARS",
      customMetricParameters: []
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/products/postProduct/postProduct.api.ts
var postProduct = createMutationEndpoint({
  route: () => `/products`,
  method: "POST",
  body: ({ body }) => JSON.stringify({
    name: body.name,
    ...body.label ? { label: body.label } : {}
  }),
  invalidateQueries: [PRODUCTS],
  mocking: {
    match: /\/api\/products$/,
    mock: {
      id: "product-1",
      name: "Example Product",
      label: "example-product",
      createdAt: "2024-06-11",
      updatedAt: "2024-06-11"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/creditNotes/creditNotes.cacheKeys.ts
var CREDIT_NOTES = "creditNotes";

// src/clients/dashboard/v20240509/endpoints/creditNotes/postCreditNote/postCreditNote.api.ts
var postCreditNoteLineItems = createMutationEndpoint({
  route: ({ id }) => `/credit-notes/${id}/line-items/from-invoice`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  config: {
    // Note: the request returns a 201 with no body (rather than a 204, with which we would expect no body),
    // so we need to handle the response transformation for this endpoint as a special case. Usually we can rely on the default handling in `createBaseApi`!
    responseTransform: () => Promise.resolve(void 0)
  },
  invalidateQueries: [CREDIT_NOTES],
  mocking: {
    match: /\/api\/credit-notes\/(.+)\/line-items\/from-invoice$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240509/endpoints/usageEvents/usageEvents.cacheKeys.ts
var DISTINCT_CUSTOMER_ALIAS_COUNT = "distinctCustomerAliasCount";

// src/clients/dashboard/v20240509/endpoints/usageEvents/getUsageEvents/getUsageEvents.api.ts
var getDistinctCustomerAliasCountForUsageEvents = createQueryEndpoint({
  route: (params) => `/usage-events/distinct-aliases-count${createQueryParams(params.filters)}`,
  queryKey: [DISTINCT_CUSTOMER_ALIAS_COUNT],
  mocking: {
    match: /\/api\/usage-events\/distinct-aliases-count(\?(.*))?$/,
    mock: {
      distinctAliasCount: "10"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/integrations/postIntegration/postIntegration.api.ts
var postIntegration = createMutationEndpoint({
  route: (params) => `/integrations/${params.service}`,
  method: "POST",
  body: () => JSON.stringify({}),
  invalidateQueries: [INTEGRATIONS],
  mocking: {
    match: /\/api\/integrations\/(.+)$/,
    mock: {
      service: "Xero",
      state: "CONNECTED",
      enabled: true,
      authFailure: false,
      defaultLedgerAccounts: {
        chargeAccount: "200",
        creditAccount: "810",
        minimumAccount: "123"
      },
      defaultTaxCodes: {
        untaxedCustomerTaxCode: "NONE"
      },
      type: "Accounting",
      syncedEntityTypes: ["CUSTOMER", "INVOICE"]
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/integrations/putIntegration/putIntegration.api.ts
var putIntegration = createMutationEndpoint({
  route: ({ service }) => `/integrations/${service}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [INTEGRATIONS],
  mocking: {
    match: /\/api\/integrations\/(.+)$/,
    mock: {
      service: "Xero",
      state: "CONNECTED",
      enabled: true,
      authFailure: false,
      defaultLedgerAccounts: {
        chargeAccount: "200",
        creditAccount: "810",
        minimumAccount: "123"
      },
      defaultTaxCodes: {
        untaxedCustomerTaxCode: "NONE"
      },
      type: "Accounting",
      syncedEntityTypes: ["CUSTOMER", "INVOICE"]
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/integrations/deleteIntegration/deleteIntegration.api.ts
var deleteIntegration = createMutationEndpoint({
  route: ({ service }) => `/integrations/${service}`,
  method: "DELETE",
  invalidateQueries: [INTEGRATIONS],
  mocking: {
    match: /\/api\/integrations\/(.+)$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240509/endpoints/integrations/deleteIntegrationLinks/deleteIntegrationLinks.api.ts
var deleteIntegrationLinks = createMutationEndpoint({
  route: ({ service }) => `/integrations/${service}/links`,
  method: "DELETE",
  invalidateQueries: [INTEGRATIONS],
  mocking: {
    match: /\/api\/integrations\/(.+)\/links$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240509/endpoints/integrations/postIntegrationAuthRedirect/postIntegrationAuthRedirect.api.ts
var postIntegrationAuthRedirect = createMutationEndpoint({
  route: ({ service }) => `/integrations/${service}/authorize/redirect`,
  method: "POST",
  invalidateQueries: [INTEGRATION_OAUTH_REDIRECT],
  mocking: {
    match: /\/api\/integrations\/(.+)\/authorize\/redirect$/,
    mock: {
      url: "http://localhost:3000"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/prices/postListPrice/postListPrice.api.ts
var postListPrice = createMutationEndpoint({
  route: () => `/list-prices`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [LIST_PRICES],
  mocking: {
    match: /\/api\/list-prices$/,
    mock: {
      id: "price-1",
      name: "Example One Time Price",
      productId: "product-1",
      structure: {
        pricingType: "ONE_TIME",
        price: "10.00"
      },
      currency: "USD",
      createdAt: "2024-06-11",
      integrationIds: [
        {
          service: "Xero",
          id: "foo"
        }
      ],
      billingFrequency: "ONE_TIME",
      billingType: "IN_ARREARS",
      customMetricParameters: []
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/prices/putListPrice/putListPrice.api.ts
var mock = {
  id: "list-price-1",
  name: "Example One Time Price",
  productId: "product-1",
  structure: {
    pricingType: "ONE_TIME",
    price: "10.00"
  },
  currency: "USD",
  createdAt: "2024-06-11",
  integrationIds: [
    {
      service: "Xero",
      id: "foo"
    }
  ],
  billingFrequency: "ONE_TIME",
  billingType: "IN_ARREARS",
  customMetricParameters: []
};
var putListPrice = createMutationEndpoint({
  route: ({ id }) => `/list-prices/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [LIST_PRICES],
  mocking: {
    match: /\/api\/list-prices\/(.+)$/,
    mock
  }
});
var putListPriceArchive = createMutationEndpoint({
  route: ({ id }) => `/list-prices/${id}/archive`,
  method: "PUT",
  invalidateQueries: [LIST_PRICES],
  mocking: {
    match: /\/api\/list-prices\/(.+)\/archive$/,
    mock
  }
});

// src/clients/dashboard/v20240509/endpoints/quotes/quotes.cacheKeys.ts
var QUOTE_SETTINGS = "quoteSettings";
var QUOTE_PDF = "quotePdf";

// src/clients/dashboard/v20240509/endpoints/quotes/downloadQuotePdf/downloadQuotePdf.api.ts
var downloadQuotePdf = createQueryEndpoint({
  route: ({ id }) => `/quotes/${id}/download`,
  config: { responseTransform: (res) => res.blob() },
  queryKey: [QUOTE_PDF],
  mocking: {
    match: /\/api\/quotes\/(.+)\/download/,
    mock: new Blob([""], { type: "application/pdf" })
  }
});

// src/clients/dashboard/v20240509/endpoints/integrations/getIntegrationAppToken/getIntegrationAppToken.api.ts
var getIntegrationAppToken = createQueryEndpoint({
  route: () => `/integrations/token`,
  queryKey: [INTEGRATION_APP_TOKEN],
  mocking: {
    match: /\/api\/integrations\/token/,
    mock: {
      token: "integration.app token"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/user/user.cacheKeys.ts
var SEQUENCE_USER = "SEQUENCE_USER";

// src/clients/dashboard/v20240509/endpoints/user/getUser/getUser.api.ts
var getUser = createQueryEndpoint({
  route: ({ id }) => `/users/${id}`,
  queryKey: [SEQUENCE_USER],
  mocking: {
    match: /\/api\/users\/(.+)/,
    mock: {
      id: "user-1",
      email: "foo@bar.com",
      sequenceAccountIds: ["account-1"],
      state: "active"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/quotes/getQuoteSettings/getQuoteSettings.api.ts
var getQuoteSettings = createQueryEndpoint({
  route: () => `/quotes/settings`,
  queryKey: [QUOTE_SETTINGS],
  mocking: {
    match: /\/api\/quotes\/settings/,
    mock: {
      isSignaturesEnabled: true,
      createdAt: "2024-06-21T00:00:00.000Z",
      updatedAt: "2024-06-21T00:00:00.000Z",
      counterSigners: [
        {
          id: "counter-signer-1",
          name: "Counter Signer",
          email: "foo@bar.com"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/quotes/putQuoteSettings/putQuoteSettings.api.ts
var putQuoteSettings = createMutationEndpoint({
  route: () => `/quotes/settings`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [QUOTE_SETTINGS],
  mocking: {
    match: /\/api\/quotes\/settings/,
    mock: {
      isSignaturesEnabled: true,
      createdAt: "2024-06-21T00:00:00.000Z",
      updatedAt: "2024-06-21T00:00:00.000Z",
      counterSigners: [
        {
          id: "counter-signer-1",
          name: "Counter Signer",
          email: "foo@bar.com"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/invoices/invoices.cacheKeys.ts
var INVOICES = "invoices";
var INVOICE_ACCOUNTING_DATE_SETTINGS = "invoiceAccountingDateSettings";

// src/clients/dashboard/v20240509/endpoints/invoices/getInvoiceAccountingDateSettings/getInvoiceAccountingDateSettings.api.ts
var getInvoiceAccountingDateSettings = createQueryEndpoint({
  route: () => `/invoice-accounting-date-settings-for-sequence-account`,
  queryKey: [INVOICE_ACCOUNTING_DATE_SETTINGS],
  mocking: {
    match: /\/api\/invoice-accounting-date-settings-for-sequence-account/,
    mock: {
      id: "settings-1",
      preference: "END_OF_BILLING_PERIOD"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/invoices/putInvoiceAccountingDateSettings/putInvoiceAccountingDateSettings.api.ts
var putInvoiceAccountingDateSettings = createMutationEndpoint({
  route: ({ id }) => `/invoice-accounting-date-settings/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [INVOICE_ACCOUNTING_DATE_SETTINGS],
  mocking: {
    match: /\/api\/invoice-accounting-date-settings\/(.+)$/,
    mock: {
      id: "settings-1",
      preference: "INTENDED_INVOICE_CREATION_DATE"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/usageMetrics/usageMetrics.cacheKeys.ts
var USAGE_METRICS = "usageMetrics";

// src/clients/dashboard/v20240509/endpoints/usageMetrics/getUsageMetrics/getUsageMetrics.api.ts
var getUsageMetrics = createQueryEndpoint({
  route: (params) => `/usage-metrics${createQueryParams(params)}`,
  queryKey: [USAGE_METRICS],
  mocking: {
    match: /\/api\/usage-metrics(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "metric-1",
          sequenceAccountId: "account-1",
          name: "Example Metric",
          description: "Example Description",
          metricType: "SIMPLE",
          eventType: "Example Event",
          aggregationType: "SUM",
          unit: "Example Unit",
          propertyFilters: {},
          propertiesToNegate: [],
          parameters: [
            {
              id: "parameter-1",
              usageMetricId: "metric-1",
              name: "Example Parameter",
              type: "INTEGER",
              description: "Example Description",
              defaultValue: "0"
            }
          ]
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/invoices/getInvoices/getInvoices.api.ts
var getInvoices = createQueryEndpoint({
  route: (params) => `/invoices${createQueryParams(params)}`,
  queryKey: [INVOICES],
  mocking: {
    match: /\/api\/invoices(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "12345",
          sequenceAccountId: "seq-1234",
          status: "DRAFT",
          currency: "GBP",
          netTotal: "1000",
          grossTotal: "1000",
          totalTax: "0",
          customerId: "cust-1234",
          customerEmails: ["foo@bar.com"],
          customerLegalCompanyName: "Test Co.",
          customerBillingAddress: {
            line1: "123 Fake St",
            town: "London",
            postcode: "SW1A 1AA",
            country: "GB"
          },
          customerShippingAddress: {
            line1: "123 Fake St",
            town: "London",
            postcode: "SW1A 1AA",
            country: "GB"
          },
          isCustomerArchived: false,
          metadata: [
            {
              key: "string",
              value: "string"
            }
          ],
          paymentStatus: "PAID",
          createdAt: "2024-01-05",
          creditNoteIds: [],
          linkedServices: [
            {
              externalId: "string",
              externalService: "Xero",
              syncTime: "2024-01-05T12:00:00Z"
            }
          ],
          accountingDate: "2024-02-01",
          customerTaxStatus: "TAX_EXEMPT",
          renderSettings: {
            paymentLinkDisplay: "HIDE_PAYMENT_LINK"
          }
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/customers/index.ts
var customers_exports = {};
__export(customers_exports, {
  deleteCustomerAlias: () => deleteCustomerAlias,
  getCustomerAliases: () => getCustomerAliases,
  getCustomerContacts: () => getCustomerContacts,
  postCustomerContact: () => postCustomerContact,
  postCustomerContactArchive: () => postCustomerContactArchive,
  putCustomerContact: () => putCustomerContact
});

// src/clients/dashboard/v20240730/endpoints/customers/customers.cacheKeys.ts
var CUSTOMER_ALIASES = "customer-aliases";

// src/clients/dashboard/v20240509/endpoints/customers/deleteCustomerAlias/deleteCustomerAlias.api.ts
var deleteCustomerAlias = createMutationEndpoint({
  route: ({ id }) => `/customer-aliases/${id}`,
  method: "DELETE",
  invalidateQueries: [CUSTOMER_ALIASES],
  mocking: {
    match: /\/api\/customer-aliases\/(.+)$/,
    mock: {
      id: "mock-alias-1",
      value: ""
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/customers/customers.cacheKeys.ts
var CUSTOMER_CONTACTS = "customerContacts";
var CUSTOMER_ALIASES2 = "customerAliases";

// src/clients/dashboard/v20240509/endpoints/customers/getCustomerAliases/getCustomerAliases.api.ts
var getCustomerAliases = createQueryEndpoint({
  route: ({ id }) => `/customers/${id}/aliases`,
  method: "GET",
  queryKey: [CUSTOMER_ALIASES2],
  mocking: {
    match: /\/api\/customers\/(.+)\/aliases$/,
    mock: {
      items: [{ id: "alias-id", value: "alias-value" }]
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/customers/getCustomerContacts/getCustomerContacts.api.ts
var getCustomerContacts = createQueryEndpoint({
  route: ({ customerId }) => `/customers/${customerId}/contacts`,
  method: "GET",
  queryKey: [CUSTOMER_CONTACTS],
  mocking: {
    match: /\/api\/customers\/(.+)\/contacts$/,
    mock: {
      items: [
        {
          id: "contact-1",
          name: "Contact 1",
          email: "contact1@foo.com",
          billingPreference: "PRIMARY",
          createdAt: "2024-06-24T00:00:00.000Z",
          updatedAt: "2024-06-24T00:00:00.000Z"
        },
        {
          id: "contact-2",
          email: "contact2@foo.com",
          name: "Contact 2",
          billingPreference: "STANDARD",
          createdAt: "2024-06-24T00:00:00.000Z",
          updatedAt: "2024-06-24T00:00:00.000Z"
        },
        {
          id: "contact-3",
          email: "contact3@foo.com",
          name: "Contact 3",
          billingPreference: "NONE",
          createdAt: "2024-06-24T00:00:00.000Z",
          updatedAt: "2024-06-24T00:00:00.000Z"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/customers/postCustomerContact/postCustomerContact.api.ts
var postCustomerContact = createMutationEndpoint({
  route: ({ customerId }) => `/customers/${customerId}/contacts`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [CUSTOMER_CONTACTS],
  mocking: {
    match: /\/api\/customers\/(.+)\/contacts$/,
    mock: {
      name: "Contact 1",
      email: "contact1@foo.com",
      billingPreference: "STANDARD",
      id: "contact-1",
      createdAt: "2024-06-24T00:00:00.000Z",
      updatedAt: "2024-06-24T00:00:00.000Z"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/customers/postCustomerContactArchive/postCustomerContactArchive.api.ts
var postCustomerContactArchive = createMutationEndpoint({
  route: ({ customerId, contactId }) => `/customers/${customerId}/contacts/${contactId}/archive`,
  method: "POST",
  invalidateQueries: [CUSTOMER_CONTACTS],
  mocking: {
    match: /\/api\/customers\/(.+)\/contacts\/(.+)\/archive$/,
    mock: {
      id: "contact-1",
      email: "contact1@foo.com",
      name: "Contact 1",
      billingPreference: "STANDARD",
      createdAt: "2024-06-24T00:00:00.000Z",
      updatedAt: "2024-06-24T00:00:00.000Z",
      archivedAt: "2024-06-25T00:00:00.000Z"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/customers/putCustomerContact/putCustomerContact.api.ts
var putCustomerContact = createMutationEndpoint({
  route: ({ customerId, contactId }) => `/customers/${customerId}/contacts/${contactId}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [CUSTOMER_CONTACTS],
  mocking: {
    match: /\/api\/customers\/(.+)\/contacts\/(.+)$/,
    mock: {
      id: "contact-1",
      email: "contact1@foo.com",
      name: "Contact 1",
      billingPreference: "STANDARD",
      createdAt: "2024-06-24T00:00:00.000Z",
      updatedAt: "2024-06-24T00:00:00.000Z",
      archivedAt: "2024-06-25T00:00:00.000Z"
    }
  }
});

// src/clients/dashboard/v20240509/endpoints/index.ts
var endpoints20240509 = {
  getDistinctCustomerAliasCountForUsageEvents,
  getIntegrations,
  getInvoiceAccountingDateSettings,
  getInvoices,
  getListPrice,
  getListPrices,
  getProduct,
  getProducts,
  getProductMetadata,
  getPrice,
  getPrices,
  getSequenceAccountSettings,
  getTaxRate,
  getTaxRates,
  getUsageMetrics,
  postCreditNoteLineItems,
  postListPrice,
  postPrice,
  postProduct,
  putInvoiceAccountingDateSettings,
  putListPrice,
  putListPriceArchive,
  postIntegration,
  putIntegration,
  deleteIntegration,
  deleteIntegrationLinks,
  postIntegrationAuthRedirect,
  downloadQuotePdf,
  getIntegrationAppToken,
  getUser,
  getQuoteSettings,
  putQuoteSettings,
  ...customers_exports
};

// src/clients/dashboard/v20240509/index.ts
var dashboardv20240509Client = createDashboardApiClient({
  endpoints: endpoints20240509,
  version: "2024-05-09"
});
export {
  dashboardv20240509Client
};
//# sourceMappingURL=index.js.map